// client/src/components/ProtectedRoute.tsx - Fixed version

import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'admin' | 'driver';
  requiredPermission?: string;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermission,
  fallbackPath = '/login' 
}) => {
  const { user, isAuthenticated, isInitialized, isLoading } = useAuthStore();

  // Wait for initialization to complete
  if (!isInitialized || isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" tip="Chargement..." />
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated || !user) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check role if required
  if (requiredRole && user.role !== requiredRole) {
    // Super admin can access admin routes
    if (requiredRole === 'admin' && user.role === 'super_admin') {
      // Allow super admin to access admin routes
    } else {
      // Redirect based on user's actual role
      const redirectPath = user.role === 'super_admin' ? '/super-admin' : '/admin';
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Check permission if required
  if (requiredPermission) {
    // Super admin has all permissions
    if (user.role !== 'super_admin') {
      const hasPermission = user.permissions?.[requiredPermission] === true;
      if (!hasPermission) {
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
          }}>
            <h1>403 - Accès refusé</h1>
            <p>Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
          </div>
        );
      }
    }
  }

  // Check if tenant is active (except for super admin)
  if (user.role !== 'super_admin' && user.tenant?.status !== 'active') {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <h1>Compte suspendu</h1>
        <p>Votre entreprise est actuellement {user.tenant?.status === 'suspended' ? 'suspendue' : 'inactive'}.</p>
        <p>Veuillez contacter votre administrateur pour plus d'informations.</p>
      </div>
    );
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default ProtectedRoute;